import { useMemo } from 'react';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import type { FilterFacet } from './Filter.types';
import { useDesignGalleryFiltersContext } from '../../DesignGalleryFiltersProvider';

/**
 * Splits the visible filter facets into the pills shown inline on the first row and the ones that
 * collapse behind the "More" pill. Both the primary filter row (`Filters`) and the full-width
 * overflow row (`FilterOverflowRow`) derive from this so they stay in sync — the split is a pure
 * function of the facet list and the current breakpoint, so calling it in two places is safe.
 *
 * The visible count is fixed per breakpoint; only the pill-showing widths (> 1024px) matter, tuned
 * so the visible pills fit alongside the search bar and preview button on one line.
 *
 * Which facets land inline is purely a matter of `FACET_ORDER` — `size` is ordered early enough
 * that it always falls before the "More" pill rather than being special-cased here.
 */
export const useResponsiveFilterOverflow = (facets: FilterFacet[]) => {
  const { getActiveFilterCountByGroupCode } = useDesignGalleryFiltersContext();
  const [visiblePillCount = 3] = useResponsive<number>({ values: { md: 1, md2: 3, md3: 4, lg: 6, lg1: 8 } }, 3);

  const filteredFacets = useMemo(() => {
    return facets.filter(facet => facet.options.filter(option => option.matchingItemCount > 0).length > 1);
  }, [facets]);

  const visibleFacets = filteredFacets.slice(0, visiblePillCount);
  const overflowFacets = filteredFacets.slice(visiblePillCount);
  const hiddenActiveCount = overflowFacets.reduce((count, facet) => count + getActiveFilterCountByGroupCode(facet.groupCode), 0);

  return { filteredFacets, visibleFacets, overflowFacets, hiddenActiveCount };
};
